import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { BrandMark } from "@/components/site/BrandMark";
import { api, type StoreStats } from "@/lib/api";
import { useLiveProperties } from "@/hooks/useLiveProperties";
import { useCountUp } from "@/hooks/useCountUp";
import { useInView } from "@/hooks/useInView";
import { cn } from "@/lib/utils";

/**
 * Hero background video, served as static assets under /video. Listed in
 * the order the browser should try them: the phone-sized clip is only
 * offered below `md`, then HEVC (roughly a third of the H.264 bytes at
 * the same quality) where the browser decodes it, then H.264 for the rest.
 */
const HERO_VIDEO_SOURCES: { src: string; type: string; media?: string }[] = [
  { src: "/video/hero-mobile.mp4", type: "video/mp4", media: "(max-width: 767px)" },
  { src: "/video/hero-hevc.mp4", type: 'video/mp4; codecs="hvc1"' },
  { src: "/video/hero-h264.mp4", type: "video/mp4" },
];

const DEVELOPER_PARTNER_COUNT = 6;

/**
 * Homepage hero — Dubai skyline, decisive headline ("Dubai Real Estate."),
 * the brand's "Turning Dreams into Addresses." line above, two CTAs and a
 * four-number credibility strip at the bottom (Live Listings, Communities,
 * AED on the Market, Developer Partners).
 */
export function Hero() {
  const { version } = useLiveProperties();
  const [stats, setStats] = useState<StoreStats | null>(null);
  const [videoReady, setVideoReady] = useState(false);
  const { ref, inView } = useInView<HTMLDivElement>();

  useEffect(() => {
    let cancelled = false;
    api
      .getStats()
      .then((s) => !cancelled && setStats(s))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [version]);

  const figures: HeroFigure[] = [
    {
      value: stats?.totalListings ?? 27,
      suffix: stats && stats.totalListings >= 27 ? "+" : undefined,
      label: "Live Listings",
    },
    {
      value: stats?.totalCommunities ?? 10,
      label: "Communities",
    },
    {
      value: stats?.totalSaleValue ?? 51_000_000,
      label: "On the Market",
      format: "aed",
    },
    {
      value: DEVELOPER_PARTNER_COUNT,
      label: "Developer Partners",
    },
  ];

  return (
    <section
      data-on-dark
      className="relative isolate overflow-hidden bg-[var(--navy-deep)]"
    >
      {/* The video — full bleed, deeper overlay. No poster image: the
          brand-navy section background shows until the video is actually
          playing, then it fades in without a flash or jump. */}
      <div className="absolute inset-0">
        <video
          className={`relative h-full w-full object-cover object-center transition-opacity duration-700 ease-out ${videoReady ? "opacity-100" : "opacity-0"}`}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          aria-hidden
          onPlaying={() => setVideoReady(true)}
          // Brighter clip per client feedback (Aug 2026); the bottom and
          // left gradient bands below stay heavy so the headline, lede,
          // and KPI strip keep their contrast.
          style={{ filter: "brightness(1.45)" }}
        >
          {HERO_VIDEO_SOURCES.map((source) => (
            <source key={source.src} {...source} />
          ))}
        </video>
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(13,20,34,0.42) 0%, rgba(13,20,34,0.14) 30%, rgba(13,20,34,0.24) 60%, rgba(13,20,34,0.8) 100%)",
          }}
        />
        <div
          aria-hidden
          className="absolute inset-0 hidden md:block"
          style={{
            background:
              "linear-gradient(90deg, rgba(13,20,34,0.35) 0%, rgba(13,20,34,0.10) 45%, rgba(13,20,34,0.0) 70%)",
          }}
        />
      </div>

      <div
        ref={ref}
        className="relative container-wide pt-[18vh] pb-10 md:pt-[22vh] md:pb-14 lg:min-h-[760px] lg:flex lg:flex-col lg:justify-end short:min-h-0 short:pt-14 short:pb-8"
      >
        {/* Eyebrow tag-line */}
        <p className="text-[16px] text-[var(--cream)] sm:text-[18px] md:text-[20px]">
          Turning Dreams into Addresses.
        </p>

        {/* The headline */}
        <h1 className="mt-3 display-hero text-[var(--text-on-dark)] max-w-[18ch] sm:mt-4">
          Dubai Real Estate.
        </h1>

        {/* Lede + CTAs */}
        <div className="mt-6 grid gap-8 md:mt-10 md:grid-cols-12 md:gap-12 short:mt-5">
          <div className="md:col-span-7 lg:col-span-7">
            <p className="text-[15.5px] leading-[1.65] text-[var(--text-on-dark-soft)] sm:text-[16px] md:text-[17.5px]">
              Your next home or investment starts here. We find it, negotiate it,
              handle every detail, and hand you the keys. Driven by deep
              expertise, tailored entirely to you.
            </p>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center md:mt-8 short:mt-5">
              <Link to="/contact" className="btn btn-primary btn-block-sm">
                Speak to an Advisor
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path
                    d="M2 7h10m-4-4 4 4-4 4"
                    stroke="currentColor"
                    strokeWidth="1.4"
                  />
                </svg>
              </Link>
              <Link to="/properties" className="btn btn-ghost btn-block-sm">
                Browse Properties
              </Link>
            </div>
          </div>
        </div>

        {/* KPI strip */}
        <ul className="mt-10 grid grid-cols-2 border-t border-[rgba(240,234,216,0.18)] sm:mt-14 md:grid-cols-4 short:mt-8">
          {figures.map((f, i) => (
            <HeroKpi key={f.label} figure={f} start={inView} index={i} />
          ))}
        </ul>
      </div>
    </section>
  );
}

interface HeroFigure {
  value: number;
  label: string;
  suffix?: string;
  format?: "aed";
}

function HeroKpi({
  figure,
  start,
  index,
}: {
  figure: HeroFigure;
  start: boolean;
  index: number;
}) {
  const animated = useCountUp(figure.value, { duration: 1600, start });
  const display = formatKpi(animated, figure.format);
  return (
    <li
      className={cn(
        "flex flex-col gap-1.5 border-b border-[rgba(240,234,216,0.18)] px-1 py-5 md:border-b-0 md:px-5 md:py-7 short:py-4",
        index < 3 && "md:border-r md:border-[rgba(240,234,216,0.18)]",
      )}
    >
      <p
        className="text-[var(--text-on-dark)] font-display whitespace-nowrap tabular-nums text-[22px] leading-none sm:text-[28px] md:text-[36px] lg:text-[44px]"
        style={{ fontVariationSettings: '"opsz" 72' }}
      >
        {display}
        {figure.suffix && (
          <span className="ml-0.5 text-[var(--gold-bright)]">
            {figure.suffix}
          </span>
        )}
      </p>
      <p className="eyebrow !text-[var(--text-on-dark-soft)]">{figure.label}</p>
    </li>
  );
}

function formatKpi(value: number, format?: "aed"): string {
  if (format === "aed") {
    if (value >= 1_000_000_000)
      return `AED ${(value / 1_000_000_000).toFixed(1)}B`;
    if (value >= 1_000_000) return `AED ${Math.round(value / 1_000_000)}M`;
    if (value >= 1_000) return `AED ${Math.round(value / 1_000)}K`;
    return `AED ${Math.round(value).toLocaleString("en-AE")}`;
  }
  return Math.round(value).toString();
}
